/**
 * Single capability card for Tietopohja IA.
 * All lenses feed the same `KbCapabilityCardModel` — one structure, one type.
 */
import {
  KB_CAPABILITY_HOME_LABEL_FI,
  KB_CAPABILITY_READINESS_LABEL_FI,
  KB_LAYER_LABEL_FI,
  type KbCapabilityCardModel,
  type KbCapabilityHome,
  type KbCapabilityLayer,
  type KbCapabilityReadiness,
  type KbRequiredSignalAnnotation,
} from "@/lib/kb-ia";
import { cardSubtle, cardTonePill } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

// Exported so instance-scope capability surfaces (e.g. Konfiguraatio/Kyvykkyydet) can render the
// identical home/layer chips as the Tietopohja type view (docs/spec/knowledge-base.md
// "Capability card contract") without forking a second chip style.
export function LayerPill({ layer }: { layer: KbCapabilityLayer }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
        layer === "monitoring" && cardTonePill.learning,
        layer === "potential" && cardTonePill.warn,
        layer === "optimization" && cardTonePill.ok,
      )}
    >
      {KB_LAYER_LABEL_FI[layer]}
    </span>
  );
}

export function HomePill({ home }: { home: KbCapabilityHome }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
        cardTonePill.empty,
      )}
    >
      {KB_CAPABILITY_HOME_LABEL_FI[home]}
    </span>
  );
}

// Instance-scope readiness pill (Kyvykkyydet) — mirrors the authoring `model.status` chip below,
// reusing the same tone scale rather than a new chip style (§2.4 one card schema instance & type
// alike). Omitted entirely when `model.readiness` is unset, so the Tietopohja type view (which
// never sets it) renders unchanged.
function ReadinessPill({ readiness }: { readiness: KbCapabilityReadiness }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
        readiness === "active" && cardTonePill.ok,
        (readiness === "needs_signal" ||
          readiness === "needs_parameter" ||
          readiness === "finding" ||
          readiness === "no_data") &&
          cardTonePill.warn,
        readiness === "learning" && cardTonePill.learning,
      )}
    >
      {KB_CAPABILITY_READINESS_LABEL_FI[readiness]}
    </span>
  );
}

function CapabilityProse({
  observation,
  explanation,
  remedy,
  description,
  legacyQuestion,
  legacyInsight,
}: {
  observation?: string;
  explanation?: string;
  remedy?: string;
  description?: string;
  legacyQuestion?: string;
  legacyInsight?: string;
}) {
  const hasThreeRole = Boolean(observation || explanation || remedy);
  if (hasThreeRole) {
    return (
      <div className="space-y-1">
        {observation && (
          <p className="text-[10px] text-foreground/90 leading-relaxed">
            <span className="font-medium text-foreground/70">Havainto: </span>
            {observation}
          </p>
        )}
        {explanation && (
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground/70">Selitys: </span>
            {explanation}
          </p>
        )}
        {remedy && (
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground/70">Toimenpide: </span>
            {remedy}
          </p>
        )}
      </div>
    );
  }
  if (description) {
    return (
      <p className="text-[10px] text-muted-foreground leading-relaxed">
        {description}
      </p>
    );
  }
  if (!legacyQuestion && !legacyInsight) return null;
  return (
    <div className="space-y-1">
      {legacyQuestion && (
        <p className="text-[10px] text-foreground/90 leading-relaxed">
          <span className="text-muted-foreground">Komento → odotus: </span>
          {legacyQuestion}
        </p>
      )}
      {legacyInsight && (
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          {legacyInsight}
        </p>
      )}
    </div>
  );
}

function MittauksetBlock({
  signals,
}: {
  signals: KbRequiredSignalAnnotation[];
}) {
  if (signals.length === 0) return null;
  return (
    <div className="text-[10px] text-muted-foreground mt-0.5">
      <p className="font-medium text-foreground/70">
        Mittaukset · {signals.length}
      </p>
      <p className="mt-1 leading-relaxed">
        {signals.map((a, i) => (
          <span key={`${a.id}-${i}`}>
            {i > 0 ? " · " : null}
            <span className="text-foreground/85">{a.id}</span>
            {a.source_fi ? (
              <span className="text-muted-foreground/80"> ({a.source_fi})</span>
            ) : null}
          </span>
        ))}
      </p>
    </div>
  );
}

// Where the rule comes from (REQ-AGT-28). The label is the source's edition and the place it was
// read — never written into the rule's own Finnish text, so a new edition changes it once.
function LahdeBlock({ labels }: { labels: string[] }) {
  if (labels.length === 0) return null;
  return (
    <p className="text-[10px] text-muted-foreground leading-relaxed">
      <span className="font-medium text-foreground/70">Lähde: </span>
      {labels.join(" · ")}
    </p>
  );
}

export function KbCapabilityCard({ model }: { model: KbCapabilityCardModel }) {
  const showPartLabels =
    model.show_part_labels ??
    (model.home === "path" || model.home === "dependency");

  return (
    <div className={cn(cardSubtle, "px-2.5 py-2 space-y-1")}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <p className="text-[11px] font-medium">{model.title_fi}</p>
        <LayerPill layer={model.layer} />
        <HomePill home={model.home} />
        {model.shared_labels_fi && model.shared_labels_fi.length > 0 && (
          <span
            className={cn(
              "shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
              cardTonePill.empty,
            )}
          >
            jaettu
          </span>
        )}
        {showPartLabels &&
          model.part_labels_fi?.map((label) => (
            <span
              key={label}
              className={cn(
                "shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
                cardTonePill.empty,
              )}
            >
              {label}
            </span>
          ))}
        {model.status && (
          <span
            className={cn(
              "shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
              model.status === "proposed" && cardTonePill.warn,
              model.status === "in-kb" && cardTonePill.ok,
              model.status === "connection" && cardTonePill.learning,
            )}
          >
            {model.status === "proposed"
              ? "ehdotus"
              : model.status === "in-kb"
                ? "KB"
                : "kytkentä"}
          </span>
        )}
        {model.readiness && <ReadinessPill readiness={model.readiness} />}
        {model.points != null && (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {model.points} p
          </span>
        )}
      </div>
      <CapabilityProse
        observation={model.observation_fi}
        explanation={model.explanation_fi}
        remedy={model.remedy_fi}
        description={model.description_fi}
        legacyQuestion={model.legacy_question_fi}
        legacyInsight={model.legacy_insight_fi}
      />
      <MittauksetBlock signals={model.signals} />
      <LahdeBlock labels={model.source_labels ?? []} />
      {model.shared_labels_fi && model.shared_labels_fi.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          Näkyy myös: {model.shared_labels_fi.join(" · ")}
        </p>
      )}
      {model.pair_label_fi && (
        <p className="text-[10px] text-muted-foreground">
          Pari (oma instanssi): {model.pair_label_fi}
        </p>
      )}
    </div>
  );
}
